import React from "react";
import { Brand } from "@/components/Brand";

const RAW_CONTACT_EMAIL = (process.env.REACT_APP_CONTACT_EMAIL || "").trim();
/** Basic mailto safety: reject newlines / weird schemes from a mis-set env. */
const CONTACT_EMAIL =
  RAW_CONTACT_EMAIL &&
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(RAW_CONTACT_EMAIL) &&
  !/[\r\n]/.test(RAW_CONTACT_EMAIL)
    ? RAW_CONTACT_EMAIL
    : "";
const GITHUB_REPO = "https://github.com/skolfitness13-cyber/Fantasy-Football";

/**
 * Short About / Contact page for AdSense reviewers and users.
 * Contact email is optional (REACT_APP_CONTACT_EMAIL at build time).
 */
export function AboutPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <header className="border-b border-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between gap-4">
          <a href="/" className="hover:opacity-80 transition-opacity" aria-label="TRYSTOMATIC home">
            <Brand size="md" />
          </a>
          <div className="flex items-center gap-4 text-xs uppercase tracking-[0.2em] font-mono-data text-[#52525B]">
            <a href="/privacy" className="hover:text-[#7C3AED] transition-colors">
              Privacy
            </a>
            <a href="/support" className="hover:text-[#7C3AED] transition-colors">
              Support
            </a>
            <a href="/" className="hover:text-[#7C3AED] transition-colors">
              Home
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-12">
        <p className="label-eyebrow">About</p>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">
          TRYSTOMATIC
        </h1>
        <p className="text-sm text-[#52525B] mt-3 max-w-prose leading-relaxed">
          Dynasty fantasy football tools built around one clear value per asset — so you can settle
          trades, scout leagues, and stay current without juggling five tabs.
        </p>

        <section className="mt-10 space-y-8 text-sm text-[#3F3F46] leading-relaxed">
          <div>
            <h2 className="font-display font-bold text-lg tracking-tight text-[#0A0A0A]">What this site is</h2>
            <p className="mt-2">
              TRYSTOMATIC is a free web app for dynasty players. Sign in with Sleeper (or browse as a
              guest) to use:
            </p>
            <ul className="mt-3 list-disc pl-5 space-y-1.5">
              <li>A trade calculator with blended rankings and pick values</li>
              <li>League roster views and recent trade history</li>
              <li>Sleeper profile lookup for any public username</li>
              <li>News focused on players you own</li>
              <li>Tier list maker and public Track views for featured leagues</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display font-bold text-lg tracking-tight text-[#0A0A0A]">How it works</h2>
            <p className="mt-2">
              Player values combine public ranking sources with optional admin blends. Sleeper data
              (profile, leagues, rosters) is read only when you choose to sign in — we never ask for
              your Sleeper password.
            </p>
          </div>

          <div>
            <h2 className="font-display font-bold text-lg tracking-tight text-[#0A0A0A]">Contact</h2>
            {CONTACT_EMAIL ? (
              <p className="mt-2">
                Questions, feedback, or privacy requests:{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-[#7C3AED] hover:underline"
                  data-testid="about-contact-email"
                >
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
            ) : (
              <p className="mt-2">
                Questions, feedback, or privacy requests: open an issue on the{" "}
                <a
                  href={GITHUB_REPO}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#7C3AED] hover:underline"
                  data-testid="about-contact-github"
                >
                  project GitHub repository
                </a>
                . Site owners can also set{" "}
                <span className="font-mono-data text-xs text-[#52525B]">REACT_APP_CONTACT_EMAIL</span>{" "}
                on the frontend build for a mailto contact.
              </p>
            )}
            <p className="mt-2">
              See also our{" "}
              <a href="/privacy" className="text-[#7C3AED] hover:underline">
                Privacy
              </a>{" "}
              page for how data and ads work, or{" "}
              <a href="/support" className="text-[#7C3AED] hover:underline">
                Support
              </a>{" "}
              if you want to tip the project.
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#0A0A0A] mt-8">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-8 text-xs text-[#52525B] font-mono-data flex flex-wrap items-center justify-between gap-2">
          <span>TRYSTOMATIC · About · {new Date().getFullYear()}</span>
          <span className="flex items-center gap-3">
            <a href="/privacy" className="hover:text-[#7C3AED] transition-colors">
              Privacy
            </a>
            <a href="/support" className="hover:text-[#7C3AED] transition-colors">
              Support
            </a>
          </span>
        </div>
      </footer>
    </div>
  );
}
